/**
 * @file module for calendar user interface
 * @author tobias.frei@notch-interactive.com
 *
 * @module components/calendar/filter-ui
 */

import model from './filter-model';
import helper from './filter-helper';

const instance = {};

let equalizeTimerRoot,
    equalizeTimerFirst,
    equalizeTimerSecond;


/**
 * set filtercloud from checkboxes
 *
 * @param {object} e - event object
 * @return {void}
 */
instance.setFilter = (e) => {
    if (e) {
        clearTimeout(equalizeTimerRoot);
        instance.setRootLevel(e);

        if (e.currentTarget.getAttribute('data-subitem')) {
            instance.setSublevel(e);
        } else if (e.currentTarget.getAttribute('data-mainitem')) {
            instance.setMainlevel(e);
        }

        equalizeTimerRoot = setTimeout(function () {
            instance.equalizeRootlevel(e);
        }, 50);
    }
};


/**
 * set rootlevel indicators
 *
 * @param {object} e - event object
 * @return {void}
 */
instance.setRootLevel = (e) => {
    // Reset filtergroup on trigger sublevel indicator
    if (e) {
        if (!e.currentTarget.classList.contains('searchfield__input')) {

            // Close sublevels
            if (e.currentTarget.parentNode.classList.contains('filterbar__filter')) {
                model.calendar.checkFirstLevelBehaviour(e.currentTarget);
            }

            // Reset levels
            model.calendar.resetFilterGroups(e.currentTarget);
        }
    }
};


/**
 * set mainLevel indicators
 *
 * @param {object} e - event object
 * @return {void}
 */
instance.setMainlevel = (e) => {
    let attrFromBase64 = JSON.parse(atob(e.currentTarget.getAttribute('data-value'))),
        key = attrFromBase64.key,
        rootFilter = attrFromBase64.filter;

    clearTimeout(equalizeTimerFirst);
    equalizeTimerFirst = setTimeout(function () {
        instance.equalizeMainlevels(rootFilter, key);
    }, 50);
};


/**
 * set sublevel indicators
 *
 * @param {object} e - event object
 * @return {void}
 */
instance.setSublevel = (e) => {
    let attrFromBase64 = JSON.parse(atob(e.currentTarget.getAttribute('data-value'))),
        parentKey = attrFromBase64.parentkey,
        rootFilter = attrFromBase64.filter;

    clearTimeout(equalizeTimerSecond);
    equalizeTimerSecond = setTimeout(function () {
        instance.equalizeSublevels(rootFilter, parentKey);
    }, 50);
};


/**
 * equalize rootlevels
 *
 * @return {void}
 */
instance.equalizeRootlevel = () => {
    let cloudClone = model.calendar.filtercloud;

    // Get rootfilter
    for (let i = 0; i < model.calendar.items.length; i++) {
        let rootname = model.calendar.items[i].key,
            amount = 0;

        // Deactivate subs
        for (let j = 0; j < cloudClone.length; j++) {
            if (cloudClone[j].filter === rootname) {
                amount++;
            }
        }

        // true -> root active, false -> root not active
        model.calendar.items[i].sublevel = amount > 0;
    }
};


/**
 * equalize mainlevels
 *
 * @param {string} rootFilter - name of rootfilter
 * @param {string} key - key of root filter
 * @return {void}
 */
instance.equalizeMainlevels = (rootFilter, key) => {
    let rootData,
        mainData;

    // Get rootfilter
    for (let i = 0; i < model.calendar.items.length; i++) {
        if (model.calendar.items[i].key === rootFilter) {
            rootData = model.calendar.items[i];
            break;
        }
    }

    // Get mainfilter
    for (let j = 0; j < rootData.items.length; j++) {
        if (rootData.items[j].key === key) {
            mainData = rootData.items[j];
            break;
        }
    }

    if (mainData.sublevel) {
        let cloudClone = model.calendar.filtercloud;
        mainData.sublevel = false;

        // Deactivate subs
        for (let k = 0; k < cloudClone.length; k++) {
            if (cloudClone[k].parentkey === key && cloudClone[k].sub === true) {
                model.calendar.filtercloud.splice(k, 1);
                k--;
            }
        }
    }

};


/**
 * equalize sublevels
 *
 * @param {string} rootFilter - name of rootfilter
 * @param {string} parentkey - key of parent filter
 * @return {void}
 */
instance.equalizeSublevels = (rootFilter, parentkey) => {
    let amount = instance.countSubfilterFromMainfilter(parentkey),
        rootData,
        mainData;

    // Get rootfilter
    for (let i = 0; i < model.calendar.items.length; i++) {
        if (model.calendar.items[i].key === rootFilter) {
            rootData = model.calendar.items[i];
            break;
        }
    }

    // Get mainfilter
    for (let j = 0; j < rootData.items.length; j++) {
        if (rootData.items[j].key === parentkey) {
            mainData = rootData.items[j];
            break;
        }
    }

    if (amount > 0) {
        mainData.sublevel = true;
        instance.removeMainfilter(parentkey);
    } else {
        mainData.sublevel = false;
    }
};


/**
 * check rootlevel of filter
 *
 * @param {object} target - check firstlevel of target
 * @return {void}
 */
instance.checkFirstLevelBehaviour = (target) => {
    let parentIndex = target.closest('*[data-index]').getAttribute('data-index');
    model.calendar.items[parentIndex].items.forEach((item) => {
        if (item.key === target.id) {
            if (item.open && !item.sublevel && !target.checked) {
                // Close subitems
                instance.showDetail({target: target.nextElementSibling});
            }
        }
    });
};


/**
 * close filter row
 *
 * @param {object} filterItem - item to close
 * @param {number} breakpoint - actual breakpoint
 * @param {boolean} callback - callback flag
 * @param {object} callbackFilter - callback item
 * @param {object} data - args for callback
 * @return {void}
 */
instance.closeRow = ({filterItem = {}, breakpoint = 5, callback = false, callbackFilter = {}, data = {}} = {}) => {
    // Get breakpoint
    let topPos,
        flyOut = filterItem.closest('.filterbar__flyout'),
        flyOutArr = [...flyOut.children];

    for (let i = 0; i < flyOutArr.length; i++) {
        if (i === 0) {
            topPos = flyOutArr[i].getBoundingClientRect().top;
        }
        if (flyOutArr[i].getBoundingClientRect().top > topPos) {
            breakpoint = i;
            break;
        }
    }

    let index = flyOutArr.indexOf(filterItem),
        rowIndex = Math.floor((index) / breakpoint) + 1;

    // Get row items
    let endRowIndex = (rowIndex * breakpoint) - 1,
        startRowIndex = (endRowIndex + 1) - breakpoint;
    if (endRowIndex > (flyOutArr.length - 1)) {
        endRowIndex = flyOutArr.length - 1;
    }

    // Close all row items
    for (let i = startRowIndex; i <= endRowIndex; i++) {
        data.items[i].openHeight = '0px';
        data.items[i].open = false;
    }

    if (callback) {
        let callbackItem = flyOutArr[endRowIndex].querySelector('.filterbar__pusher');
        callbackItem.args = [{
            filterItem: callbackFilter,
            breakpoint: breakpoint,
            data: data
        }];
        callbackItem.callbackFunctions = [instance.openRow];
        callbackItem.addEventListener('transitionend', helper.transitionListener, false);
    }

    data.items[endRowIndex].pushHeight = '0px';
    data.items[endRowIndex].push = false;
};


/**
 * open row
 *
 * @param {object} filterItem - item to open
 * @param {number} breakpoint - actual breakpoint
 * @param {boolean} callback - callback flag
 * @param {object} callbackFilter - callback item
 * @param {object} data - args for callback
 * @return {void}
 */
instance.openRow = ({filterItem = {}, breakpoint = 5, callback = false, callbackFilter = {}, data = {}} = {}) => {

    // Get breakpoint
    let topPos,
        flyOut = filterItem.closest('.filterbar__flyout'),
        flyOutArr = [...flyOut.children];

    for (let i = 0; i < flyOutArr.length; i++) {
        if (i === 0) {
            topPos = flyOutArr[i].getBoundingClientRect().top;
        }
        if (flyOutArr[i].getBoundingClientRect().top > topPos) {
            breakpoint = i;
            break;
        }
    }

    let filterWrap = filterItem.querySelector('.filterbar__detailmenuwrap'),
        height = filterWrap.offsetHeight,
        index = flyOutArr.indexOf(filterItem),
        rowIndex = Math.floor((index) / breakpoint) + 1;

    // Get row items
    let endRowIndex = (rowIndex * breakpoint) - 1,
        startRowIndex = (endRowIndex + 1) - breakpoint;
    if (endRowIndex > (flyOutArr.length - 1)) {
        endRowIndex = flyOutArr.length - 1;
    }

    // Set active subitem overlay and height to all row overlay
    data.items[index].open = true;
    for (let i = startRowIndex; i <= endRowIndex; i++) {
        data.items[i].openHeight = height + 'px';
    }

    if (callback) {
        let callbackItem = flyOutArr[endRowIndex].querySelector('.filterbar__pusher');
        callbackItem.args = [{
            filterItem: callbackFilter,
            breakpoint: breakpoint,
            data: data
        }];
        callbackItem.callbackFunctions = [instance.closeRow];
        callbackItem.addEventListener('transitionend', helper.transitionListener, false);
    }

    // Push row
    data.items[endRowIndex].push = true;
    data.items[endRowIndex].pushHeight = height + 'px';

    // Reset all other overlay in row
    data.items.forEach((filter) => {
        let i = data.items.indexOf(filter);
        if (i !== index) {
            filter.open = false;
        }
    });
};


/**
 * show details on tile
 *
 * @param {object} e - click event
 * @return {void}
 */
instance.showDetail = (e) => {
    let target;

    if (!e.target.classList.contains('filterbar__filter')) {
        target = e.target.closest('.filterbar__filter');
    } else {
        target = e.target;
    }

    // Get breakpoint
    let topPos,
        flyOut = target.closest('.filterbar__flyout'),
        flyOutArr = [...flyOut.children],
        breakpoint;

    for (let i = 0; i < flyOutArr.length; i++) {
        if (i === 0) {
            topPos = flyOutArr[i].getBoundingClientRect().top;
        }
        if (flyOutArr[i].getBoundingClientRect().top > topPos) {
            breakpoint = i;
            break;
        }
    }

    let filterbarIndex = target.closest('.filterbar__item').getAttribute('data-index'),
        data = model.calendar.items[filterbarIndex],
        index = flyOutArr.indexOf(target),
        detailRow = Math.floor((index) / breakpoint) + 1,
        pushIndex = (detailRow * breakpoint) - 1;

    // Get last item if row not full
    if (pushIndex > flyOutArr.length - 1) {
        pushIndex = flyOutArr.length - 1;
    }

    // Check if other row is open
    for (let i = 0; i < data.items.length; i++) {
        if (data.items[i].push) {
            if (i !== pushIndex) {
                instance.closeRow({
                    filterItem: flyOutArr[i],
                    breakpoint: breakpoint,
                    callback: true,
                    callbackFilter: target,
                    data: data
                });
                return;
            }
        }
    }

    // If same row or no row open
    if (data.items[index].open === true && data.items[index].openHeight !== '0px') {
        instance.closeRow({
            filterItem: target,
            breakpoint: breakpoint,
            data: data
        });
    } else {
        instance.openRow({
            filterItem: target,
            breakpoint: breakpoint,
            data: data
        });
    }
};


/**
 * open filter navigation
 *
 * @param {object} e - click event
 * @return {void}
 */
instance.openNav = (e) => {
    let index = Array.from(e.currentTarget.closest('*[data-payload]').children).indexOf(e.currentTarget.closest('*[data-filteritem]'));

    // Check reset if labelbox hit
    if (e.target.classList.contains('filterbar__itemlabelbox')) {
        model.calendar.resetFilter(index);
        return;
    }

    model.calendar.items[index].isOpen = !model.calendar.items[index].isOpen;
    for (let i = 0; i < model.calendar.items.length; i++) {
        if (i === index) continue;
        model.calendar.items[i].isOpen = false;
    }

    if (model.calendar.items[index].isOpen) {
        document.addEventListener('click', helper.bodyclick, false);
    } else {
        document.removeEventListener('click', helper.bodyclick, false);
    }
};


/**
 * close filter navigation
 *
 * @return {void}
 */
instance.closeNav = () => {
    for (let i = 0; i < model.calendar.items.length; i++) {
        model.calendar.items[i].isOpen = false;
    }
};


/**
 * close all filter sublevel collabsible
 *
 * @return {void}
 */
instance.closeAllSublevels = () => {
    model.calendar.items.forEach((item) => {
        item.items.forEach((subitem) => {
            if (subitem.open) {
                subitem.open = false;
                subitem.openHeight = 0;
            }
            if (subitem.push) {
                subitem.push = false;
                subitem.pushHeight = 0;
            }
        });
    });

    let openNavs = [...document.querySelectorAll('.filterbar__detailmenu')];
    openNavs.forEach((openNav) => {
        openNav.setAttribute('style', 'height: 0px;');
    });
};


/**
 * close all sublevels on targeted filter
 *
 * @param {number} index - index of filter to close sublevels
 * @return {void}
 */
instance.closeAllSublevelsOnFilter = (index) => {
    model.calendar.items[index].items.forEach((subitem) => {
        if (subitem.open) {
            subitem.open = false;
            subitem.openHeight = 0;
        }
        if (subitem.push) {
            subitem.push = false;
            subitem.pushHeight = 0;
        }
    });

    let filter = document.querySelector('*[data-filteritem][data-index="' + index + '"]'),
        openNavs = [...filter.querySelectorAll('.filterbar__detailmenu')];

    openNavs.forEach((openNav) => {
        openNav.setAttribute('style', 'height: 0px;');
    });
};


/**
 * reset all subfilter on targeted mainfilter
 *
 * @param {number} index - index of filter to reset  sublevels
 * @return {void}
 */
instance.resetFilter = (index) => {
    if (model.calendar.items[index].sublevel) {

        // Cancel filter items in cloud
        for (let i = 0; i < model.calendar.filtercloud.length; i++) {
            if (model.calendar.filtercloud[i].filter === model.calendar.items[index].key) {
                model.calendar.filtercloud.splice(i, 1);
                i--;
            }
        }

        // Cancel filtergroup sublevel
        model.calendar.items[index].items.forEach((item) => {
            if (item.sublevel) {
                item.sublevel = false;
            }
        });

        // Cancel filter sublevel
        model.calendar.items[index].sublevel = false;
        model.calendar.closeAllSublevelsOnFilter(index);
    }

    model.calendar.setFilter();
};


/**
 * reset all sublevels on targeted rootfilter
 *
 * @param {number} target - index of rootfilter to reset
 * @return {void}
 */
instance.resetFilterGroups = (target) => {
    let parentIndex = target.closest('*[data-index]').getAttribute('data-index');
    model.calendar.items[parentIndex].items.forEach((item) => {
        if (item.key === target.id) {
            if (item.sublevel) {
                for (let i = 0; i < model.calendar.filtercloud.length; i++) {
                    if (model.calendar.filtercloud[i].parentkey === target.id) {
                        // Remove
                        model.calendar.filtercloud.splice(i, 1);
                        i--;
                    }
                }
                item.sublevel = false;
            }
        }
    });
};


/**
 * count subfilters of a mainfilter in the filtercloud
 *
 * @param {number} parentkey - key of mainfilter
 * @return {number} - amount of subfilter in filtercloud
 */
instance.countSubfilterFromMainfilter = (parentkey) => {
    let amount = 0;
    for (let i = 0; i < model.calendar.filtercloud.length; i++) {
        if (model.calendar.filtercloud[i].parentkey === parentkey && model.calendar.filtercloud[i].sub === true) {
            amount++;
        }
    }
    return amount;
};


/**
 * remove mainfilter in filtercloud
 *
 * @param {number} parentkey - key of mainfilter
 * @return {void}
 */
instance.removeMainfilter = (parentkey) => {
    let cloudClone = model.calendar.filtercloud;
    for (let i = 0; i < cloudClone.length; i++) {
        if (cloudClone[i].key === parentkey && cloudClone[i].sub === false) {
            model.calendar.filtercloud.splice(i, 1);
            i--;
        }
    }
};


export default instance;
